import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Modal, Button, Badge } from "react-bootstrap";
import Breadcrumb from "../../../../components/layout/Breadcrumb";
import { fetchPolicies } from "../../../../redux/slice/policiesActiveSlice";
import { postPolicyAccept } from "../../../../redux/slice/postPolicyAcceptSlice";
import companyLogo from "../../../../assets/img/company_logo.png";
import {toast, ToastContainer } from "react-toastify";
import { fetchAllConfiguration } from "../../../../redux/slice/getAllConfigurationListSlice";
import { getAllUploadedFile } from '../../../../redux/slice/filesUploadSlice';

const MIN_READ_TIME = 20;
const EmpDocList = [
  { key: 'passportPhoto', label: 'Profile Photo' },
  { key: 'panCard', label: 'PAN Card' },
  { key: 'aadhaarCard', label: 'Aadhaar Card' },
  { key: 'passportFrontImage', label: 'Passport Front Page' },
  { key: 'passportLastPageImage', label: 'Passport Last Page' },
  { key: 'illnessDocument', label: 'Medical/Illness Document' },

  { key: '10thMarksheet', label: '10th Standard' },
  { key: '12thMarksheet', label: '12th Standard' },

  { key: 'expCertFile', label: 'Experience Certificate' },
  { key: 'relievingLetterFile', label: 'Relieving Letter' },
  { key: 'offerLetter', label: 'Offer Letter' },
  { key: 'lastThreeMonthSalarySlip1', label: 'Salary Slip 1' },
  { key: 'lastThreeMonthSalarySlip2', label: 'Salary Slip 2' },
  { key: 'lastThreeMonthSalarySlip3', label: 'Salary Slip 3' },
  { key: 'lastThreeMonthBankStatement', label: '3-Month Bank Statement' }
]
export default function Policies() {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [readSeconds, setReadSeconds] = useState(0);
  const [isScrollCompleted, setIsScrollCompleted] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  let acceptedPolicies=0;

  const timerRef = useRef(null);
  const contentWrapperRef = useRef(null);

  const { list = [], loading } = useSelector((state) => state.policies);
  const { loading: submitLoading } = useSelector(
    (state) => state.postPolicyAccept
  );

  const InitialPermissionData = useSelector(
    (state) => state.persistedReducer?.forms?.savedPermissions
  );
  const { allFiles, fetchAllStatus } = useSelector((state) => state.filesUpload);
  const { configurations } = useSelector(
    (state) => state.Configurations
  );

  const { userId } = InitialPermissionData ?? {};
  const selfUserId = params?.id ?? location?.state?.id ?? userId;

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const stripHtml = useCallback((html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }, []);

  const checkScrollBottom = useCallback(() => {
    const el = contentWrapperRef.current;
    if (!el) return false;
    return Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
  }, []);

  const activePolicies = useMemo(
    () => list.filter((p) => p.status === true),
    [list]
  );

  const isLongTitle = useMemo(() => {
    const title = selectedPolicy?.title || "";
    return title.trim().split(/\s+/).length > 5;
  }, [selectedPolicy]);

  const alreadyAccepted = selectedPolicy?.accepted === true;
  const canAccept = useMemo(
    () => readSeconds >= MIN_READ_TIME && isScrollCompleted,
    [readSeconds, isScrollCompleted]
  );
  const showAcceptSection = !alreadyAccepted;
  useEffect(() => {
    dispatch(fetchPolicies());
  }, [dispatch]);
    useEffect(() => {
      if (userId) {
        dispatch(getAllUploadedFile(userId));
        dispatch(fetchAllConfiguration({ "config": "REQUIRED_DOCUMENTS" ?? "" })).unwrap();
      }
    }, [dispatch, userId]);

  useEffect(() => {
    if (!selectedPolicy || selectedPolicy.accepted) return;

    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setReadSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [selectedPolicy]);

  const handleScroll = () => {
    if (checkScrollBottom()) {
      setIsScrollCompleted(true);
    }
  };

  const resetPolicyState = () => {
    clearInterval(timerRef.current);
    setReadSeconds(0);
    setIsScrollCompleted(false);
    setIsAccepted(false);
  };

  const closeModal = () => {
    resetPolicyState();
    setSelectedPolicy(null);
  };
const labelLookup = Object.fromEntries(
  EmpDocList.map(doc => [doc.key.toLowerCase(), doc.label])
);
  const requiredDocMessage = configurations?.find(
  (config) => config.configName === "EMPLOYEE_REQUIRED_DOCUMENT"
)?.configValue || "";
const uploadedFileTypes = new Set(
  (allFiles || []).map(file => file.fileType.toLowerCase())
);
const requiredDocsStatus = requiredDocMessage
  .split(',')
  .map(key => {
    const trimmedKey = key.trim().toLowerCase();
    const label = labelLookup[trimmedKey];
    
    if (!label) return null;

    return {
      label: label,
      isUploaded: uploadedFileTypes.has(trimmedKey)
    };
  })
  .filter(Boolean);
  const areAllDocsUploaded = requiredDocsStatus.length > 0 && requiredDocsStatus.every(doc => doc.isUploaded);
  const handleSubmit = async () => {
    if (!canAccept || !selectedPolicy || !selfUserId) return;
    const payload = {
      policyId: selectedPolicy.id,
      userId: selfUserId,
      readTime: readSeconds,
      accepted: true,
    };

    const result = await dispatch(postPolicyAccept(payload));
    if (postPolicyAccept.fulfilled.match(result)) {
      closeModal();
      const currentCount = acceptedPolicies;

    const newCount = currentCount + 1;
    localStorage.setItem("acceptedPoliciesCount", newCount);
    if (newCount >= activePolicies.length) {
      toast.success("All policies accepted!", { className: "toast-success-custom", });
      
      setTimeout(() => {
        if(areAllDocsUploaded){
          toast.info("Please logout and login again to access the Dashboard", {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          position: "top-center",
          theme: "colored"
        });
        }else{
        toast.info("Upload the required documents, then re-login to access your dashboard.", {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          position: "top-center",
          theme: "colored"
        });
        }
        
      }, 1000);
    }
      dispatch(fetchPolicies());
    }
  };

  return (
    <div className="container-fluid" style={{ marginBottom: "30px" }}>

      <div className="section-body mt-2">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body pt-0 pb-0">
              {loading && <p>Loading policies...</p>}

              <div className="emppolicies-container table-responsive m-0">
                <table className="table table-bordered table-hover table-vcenter text-nowrap mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>ID</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Created At</th>
                      <th>Status</th>
                      <th style={{ width: 55, textAlign: "center" }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {!loading && activePolicies.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <p className="server-error-msg">
                            -- No policies found --
                          </p>
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      activePolicies.length > 0 &&
                      activePolicies.map((policy) => {
                        const description = stripHtml(policy.contentHtml);
                        const shortDesc =
                          description.length > 100
                            ? description.slice(0, 100) + "..."
                            : description;
                            if(policy.accepted){
                              acceptedPolicies++;
                            }
                        
                        return (
                          <tr key={policy.id}>
                            <td>{policy.id}</td>
                            <td className="text-left policy-title">
                              {policy.title}
                            </td>
                            <td className="text-left policy-content">
                              {shortDesc}
                            </td>
                            <td>{formatDate(policy.createdAt)}</td>
                            <td>
                              {policy.accepted ? (
                                <Badge bg="success">Accepted</Badge>
                              ) : (
                                <Badge bg="warning" text="dark">
                                  Pending
                                </Badge>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <button className="primary-btn ml-4 btn btn-primary"
                                onClick={() => setSelectedPolicy(policy)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <Modal
            show={!!selectedPolicy}
            onHide={closeModal}
            size="xl"
            centered
            scrollable
            className="policy-view-modal"
          >
            <Modal.Header>
              <Modal.Title className="policy-header-title">
                {selectedPolicy?.title || "Policy Preview"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ background: "#e9ecef" }}>
              <div
                className="policy-scroll-wrapper"
                ref={contentWrapperRef}
                onScroll={handleScroll}
                style={{
                  maxHeight: "65vh",
                  overflowY: "auto",
                  paddingRight: "10px",
                }}
              >
                <div className="preview-header d-flex justify-content-between align-middle">
                  <div className="header-logo">
                    <img
                      className="company-logo mb-3"
                      src={companyLogo}
                      alt="Logo"
                      height="40"
                    />
                    <p>
                      <strong>Created By:</strong>{" "}
                      {selectedPolicy?.createdBy || "--"}
                    </p>
                    <p>
                      <strong>Created At:</strong>{" "}
                      {formatDate(selectedPolicy?.createdAt)}
                    </p>
                  </div>

                  <div className="header-right">
                    {!isLongTitle && (
                      <h4 className="m-0">{selectedPolicy?.title}</h4>
                    )}
                    <p className="mb-3">
                      <strong>Version: </strong>
                      {selectedPolicy?.version || "--"}
                    </p>

                    {selectedPolicy?.updatedAt && (
                      <p>
                        <strong>Updated By: </strong>
                        {selectedPolicy?.updatedBy || "-"}
                      </p>
                    )}
                    {selectedPolicy?.updatedAt && (
                      <p>
                        <strong>Updated At:</strong>{" "}
                        {formatDate(selectedPolicy?.updatedAt)}
                      </p>
                    )}
                  </div>
                </div>

                {isLongTitle && (
                  <div className="policy-long-title mb-0">
                    <h3 className="mb-0">{selectedPolicy?.title}</h3>
                    <hr />
                  </div>
                )}

                <div
                  id="policy-preview"
                  className="policy-preview"
                  dangerouslySetInnerHTML={{
                    __html: selectedPolicy?.contentHtml || "",
                  }}
                />
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                className="btn-cancel-cta"
                onClick={closeModal}
              >
                Close
              </Button>

              {showAcceptSection&&(<div className="policy-accept mt-3">
                <div className="policy-timer mb-2">
                  <strong>Reading Time:</strong>{" "}
                  <span className="timer-clock">
                    ⏱ {formatTime(readSeconds)}
                  </span>
                  {!canAccept && (
                    <span className="ms-2 text-muted">
                      (Minimum {MIN_READ_TIME} seconds + scroll required)
                    </span>
                  )}
                </div>
                {canAccept && (
                  <label>
                    <input
                      type="checkbox"
                      className="mr-1"
                      disabled={!canAccept}
                      checked={isAccepted}
                      onChange={(e) => setIsAccepted(e.target.checked)}
                    />
                    I have read and accept this policy
                  </label>
                )}
                {!canAccept && (<p className="policy-hint mt-1">Please scroll till end and read document</p>)}
              </div>)}

              {showAcceptSection && (
                <Button
                  className="primary-btn ml-4"
                  disabled={!isAccepted || submitLoading}
                  onClick={handleSubmit}
                >
                  {submitLoading ? "Submitting..." : "Accept & Submit"}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        </div>
      </div>
      <ToastContainer/>
    </div>
  );
}
